import { CloudUpload, CheckCircle, Error, Info } from '@mui/icons-material';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  parseCSV,
  matchFilesWithSpreadsheet,
  readFileAsText,
  SpreadsheetRow,
  ParsedSpreadsheet,
} from '../../utilities/csvParser';
import { enqueueUniqueSnackbar } from '../../utilities/snackbarUtils';

interface SpreadsheetUploadProps {
  onSpreadsheetProcessed: (matches: Array<{ file: File; row: SpreadsheetRow }>) => void;
  mediaFiles: File[];
  onMediaFilesChange: (files: File[]) => void;
}

const SpreadsheetUpload: React.FC<SpreadsheetUploadProps> = ({
  onSpreadsheetProcessed,
  mediaFiles,
  onMediaFilesChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [spreadsheetFile, setSpreadsheetFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedSpreadsheet | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Dropzone for CSV files
  const {
    getRootProps: getCsvRootProps,
    getInputProps: getCsvInputProps,
    isDragActive: isCsvDragActive,
  } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    onDrop: handleSpreadsheetDrop,
  });

  // Dropzone for media files
  const {
    getRootProps: getMediaRootProps,
    getInputProps: getMediaInputProps,
    isDragActive: isMediaDragActive,
  } = useDropzone({
    accept: {
      'video/*': [],
      'audio/*': [],
      'image/*': [],
    },
    multiple: true,
    onDrop: handleMediaDrop,
  });

  async function handleSpreadsheetDrop(acceptedFiles: File[]) {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setSpreadsheetFile(file);
    setProcessing(true);

    try {
      const content = await readFileAsText(file);
      const parsed = parseCSV(content);
      
      setParsedData(parsed);
      
      if (parsed.errors.length > 0) {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `CSV parsed with ${parsed.errors.length} warnings. Click to review.`,
          { variant: 'warning' }
        );
        setShowPreview(true);
      } else {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Successfully parsed ${parsed.rows.length} rows from CSV`,
          { variant: 'success' }
        );
        
        // Auto-match if we already have media files
        if (mediaFiles.length > 0) {
          processMatching(parsed);
        }
      }
                 } catch (error: unknown) {
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = (error as Error).message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Failed to parse CSV: ${errorMessage}`,
          { variant: 'error' }
        );
     } finally {
      setProcessing(false);
    }
  }

  function handleMediaDrop(acceptedFiles: File[]) {
    const newFiles = [...mediaFiles, ...acceptedFiles];
    onMediaFilesChange(newFiles);
    
    // Auto-match if we have parsed data
    if (parsedData && parsedData.rows.length > 0) {
      processMatching(parsedData, newFiles);
    }
  }

  const processMatching = useCallback((data: ParsedSpreadsheet, files: File[] = mediaFiles) => {
    const { matches, unmatched } = matchFilesWithSpreadsheet(files, data.rows);
    
    onSpreadsheetProcessed(matches);
    
    enqueueUniqueSnackbar(
      enqueueSnackbar,
      `Matched ${matches.length} files with spreadsheet data. ${unmatched.length} files unmatched.`,
      { variant: matches.length > 0 ? 'success' : 'info' }
    );
  }, [mediaFiles, onSpreadsheetProcessed, enqueueSnackbar]);

  const clearSpreadsheet = () => {
    setSpreadsheetFile(null);
    setParsedData(null);
    setShowPreview(false);
  };

  const clearMediaFiles = () => {
    onMediaFilesChange([]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Typography variant="h6" gutterBottom>
        Spreadsheet + Media Upload
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Upload a CSV file with metadata and drag media files to automatically populate names and descriptions.
      </Typography>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* CSV Upload Section */}
        <Paper 
          sx={{ 
            flex: 1, 
            minWidth: '300px',
            p: 2,
            border: spreadsheetFile ? '2px solid #4caf50' : '2px dashed #ccc',
            bgcolor: isCsvDragActive ? 'action.hover' : 'background.paper'
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            1. Upload Spreadsheet
          </Typography>
          
          {!spreadsheetFile ? (
            <Box
              {...getCsvRootProps()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <input {...getCsvInputProps()} />
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                {isCsvDragActive ? 'Drop CSV file here' : 'Drag CSV file here or click to browse'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports: CSV, Excel (.xls, .xlsx)
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body1">{spreadsheetFile.name}</Typography>
              </Box>
              
              {parsedData && (
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={`${parsedData.rows.length} rows`} 
                    size="small" 
                    color="primary" 
                    sx={{ mr: 1 }} 
                  />
                  {parsedData.errors.length > 0 && (
                    <Chip 
                      label={`${parsedData.errors.length} warnings`} 
                      size="small" 
                      color="warning" 
                    />
                  )}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => setShowPreview(true)}>
                  Preview Data
                </Button>
                <Button size="small" onClick={clearSpreadsheet}>
                  Remove
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Media Files Section */}
        <Paper 
          sx={{ 
            flex: 1, 
            minWidth: '300px',
            p: 2,
            border: mediaFiles.length > 0 ? '2px solid #4caf50' : '2px dashed #ccc',
            bgcolor: isMediaDragActive ? 'action.hover' : 'background.paper'
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            2. Upload Media Files
          </Typography>
          
          {mediaFiles.length === 0 ? (
            <Box
              {...getMediaRootProps()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <input {...getMediaInputProps()} />
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                {isMediaDragActive ? 'Drop media files here' : 'Drag media files here or click to browse'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports: Videos, Audio, Images
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body1">{mediaFiles.length} files selected</Typography>
              </Box>
              
              <List dense sx={{ maxHeight: 150, overflow: 'auto', mb: 2 }}>
                {mediaFiles.slice(0, 5).map((file, index) => (
                  <ListItem key={index} dense>
                    <ListItemText 
                      primary={file.name}
                      secondary={`${(file.size / 1024 / 1024).toFixed(1)} MB`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {mediaFiles.length > 5 && (
                  <ListItem dense>
                    <ListItemText secondary={`... and ${mediaFiles.length - 5} more files`} />
                  </ListItem>
                )}
              </List>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.accept = 'video/*,audio/*,image/*';
                    input.onchange = (e) => {
                      const files = Array.from((e.target as HTMLInputElement).files || []);
                      handleMediaDrop(files);
                    };
                    input.click();
                  }}
                >
                  Add More
                </Button>
                <Button size="small" onClick={clearMediaFiles}>
                  Clear All
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Process Button */}
      {spreadsheetFile && parsedData && mediaFiles.length > 0 && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => processMatching(parsedData)}
            disabled={processing}
          >
            Process & Match Files
          </Button>
        </Box>
      )}

      {/* Warnings/Errors */}
      {parsedData && parsedData.errors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" gutterBottom>
            CSV parsing issues detected:
          </Typography>
          <List dense>
            {parsedData.errors.slice(0, 3).map((error, index) => (
              <ListItem key={index} dense>
                <ListItemIcon><Error fontSize="small" /></ListItemIcon>
                <ListItemText primary={error} />
              </ListItem>
            ))}
            {parsedData.errors.length > 3 && (
              <ListItem dense>
                <ListItemText secondary={`... and ${parsedData.errors.length - 3} more issues`} />
              </ListItem>
            )}
          </List>
        </Alert>
      )}

      {/* Preview Dialog */}
      <Dialog 
        open={showPreview} 
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Spreadsheet Preview
          {parsedData && (
            <Typography variant="body2" color="text.secondary">
              {parsedData.rows.length} rows, {parsedData.headers.length} columns
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {parsedData && (
            <>
              {parsedData.errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>Issues found:</Typography>
                  <List dense>
                    {parsedData.errors.map((error, index) => (
                      <ListItem key={index} dense>
                        <ListItemText primary={error} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Filename</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData.rows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.name || <em>Empty</em>}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.description ? 
                              row.description.length > 50 ? 
                                `${row.description.substring(0, 50)}...` : 
                                row.description 
                              : <em>Empty</em>
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>{row.filename || <em>No file</em>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpreadsheetUpload; 